import React, { useEffect, useState } from "react";
import { api, imgUrl, money } from "../../lib/api";
import { toast } from "sonner";
import DataTable, { Pagination } from "../../components/admin/DataTable";

/** Line items for one order.
 *
 * Everything here already ships in the `/admin/orders` response: `order.items`
 * is the snapshot written at checkout (title, image, price, size, colour, qty)
 * and `order.seller_splits` carries each seller's shop_name. The list view only
 * ever rendered `item_count`, so the products themselves were never shown
 * anywhere in the admin — there is no admin order-detail page either.
 *
 * Laid out as cards rather than a nested table: the parent table scrolls
 * horizontally on narrow screens, and a nested table would inherit that and
 * hide the detail behind a sideways scroll on exactly the device in the report.
 */
function OrderItems({ order }) {
  const items = order?.items || [];
  const shopById = Object.fromEntries(
    (order?.seller_splits || []).map((sp) => [sp.seller_id, sp.shop_name]),
  );
  if (!items.length) {
    return (
      <div className="p-4 text-xs text-[hsl(var(--kb-muted))]" data-testid="order-items-empty">
        No line items recorded for this order.
      </div>
    );
  }
  return (
    <div className="p-4 space-y-3" data-testid={`order-items-${order.id}`}>
      <div className="overline text-[10px]">Items ({items.length})</div>
      {items.map((it, idx) => {
        const variant = [it.size, it.color].filter(Boolean).join(" · ");
        const qty = Number(it.qty) || 0;
        const unit = Number(it.price) || 0;
        return (
          <div
            key={`${it.product_id}-${it.size || ""}-${it.color || ""}-${idx}`}
            className="flex gap-3 items-start bg-white border border-[hsl(var(--kb-border))] p-3"
            data-testid={`order-item-${order.id}-${idx}`}
          >
            <div className="w-14 h-14 shrink-0 bg-[hsl(var(--kb-surface-alt))] overflow-hidden">
              {it.image ? (
                <img
                  src={imgUrl(it.image)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm break-words" data-testid={`order-item-title-${order.id}-${idx}`}>
                {it.title || it.product_id}
              </div>
              {variant && (
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5" data-testid={`order-item-variant-${order.id}-${idx}`}>
                  {variant}
                </div>
              )}
              {shopById[it.seller_id] && (
                <div className="text-[11px] text-[hsl(var(--kb-muted))] mt-0.5">
                  Sold by {shopById[it.seller_id]}
                </div>
              )}
              {/* Wraps instead of overflowing on a 375px viewport. */}
              <div className="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Qty: <strong>{qty}</strong></span>
                <span>Unit: {money(unit)}</span>
                <span>Total: <strong>{money(unit * qty)}</strong></span>
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs pt-1">
        <span>Subtotal: {money(order.subtotal)}</span>
        {order.coupon_code && (
          <span data-testid={`order-coupon-${order.id}`}>Coupon: <strong>{order.coupon_code}</strong></span>
        )}
        {!!order.discount && <span>Discount: −{money(order.discount)}</span>}
        {!!order.coins_discount && <span>Coins: −{money(order.coins_discount)}</span>}
        {order.shipping != null && <span>Shipping: {money(order.shipping)}</span>}
        <span className="font-medium">Order total: {money(order.total)}</span>
      </div>
    </div>
  );
}

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const titleCase = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const PAYMENT_METHOD_LABEL = { cod: "Cash on delivery", online: "Online (Razorpay)" };

/** One labelled field. Renders nothing when the value is absent, so the panel
 * never shows fabricated or placeholder data for fields an order doesn't carry. */
function Field({ label, children, testid }) {
  if (children == null || children === "" || children === false) return null;
  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">{label}</div>
      <div className="break-words" data-testid={testid}>{children}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-3 space-y-2" data-testid={testid}>
      <div className="overline text-[10px]">{title}</div>
      {children}
    </div>
  );
}

/** Full order detail for the admin.
 *
 * Every field comes from the order document `/admin/orders` already returns —
 * nothing is fetched separately. The delivery address is EMBEDDED in the order
 * at checkout rather than joined from the customer's address book, so it is
 * intrinsically the address for this order: it can't drift to another
 * customer, and a customer later editing their saved addresses doesn't
 * rewrite where an existing order ships.
 *
 * Customer and delivery come first because they are what an operator acts on.
 * Grid stacks to one column on phones.
 */
function OrderDetails({ order }) {
  const a = order.shipping_address || order.address || {};
  const cityLine = [a.city, a.state].filter(Boolean).join(", ");
  const history = Array.isArray(order.status_history) ? order.status_history : [];
  const isOnline = order.payment_method && order.payment_method !== "cod";

  return (
    <div className="p-4 space-y-3" data-testid={`order-details-${order.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Section title="Customer" testid={`order-customer-${order.id}`}>
          <Field label="Name" testid={`order-customer-name-${order.id}`}>{a.name}</Field>
          <Field label="Phone" testid={`order-customer-phone-${order.id}`}>
            {a.phone && <a href={`tel:${a.phone}`} className="underline">{a.phone}</a>}
          </Field>
          <Field label="Customer ID">
            {order.user_id && <span className="font-mono text-xs">{order.user_id}</span>}
          </Field>
        </Section>

        <Section title="Delivery address" testid={`order-address-${order.id}`}>
          {a.label && (
            <div className="text-[10px] uppercase tracking-wider inline-block border border-[hsl(var(--kb-border))] px-1.5 py-0.5">
              {a.label}
            </div>
          )}
          <div className="text-sm leading-relaxed" data-testid={`order-address-text-${order.id}`}>
            {a.line1 && <div>{a.line1}</div>}
            {a.line2 && <div>{a.line2}</div>}
            {cityLine && <div>{cityLine}</div>}
            {a.pincode && <div>PIN <strong>{a.pincode}</strong></div>}
            {a.country && <div className="text-[hsl(var(--kb-muted))]">{a.country}</div>}
          </div>
        </Section>

        <Section title="Order" testid={`order-info-${order.id}`}>
          <Field label="Order number">
            <span className="font-mono text-xs">{order.order_number || order.id}</span>
          </Field>
          <Field label="Placed">{fmtDateTime(order.placed_at || order.created_at)}</Field>
          <Field label="Current status">{titleCase(order.status)}</Field>
          <Field label="Shipped">{fmtDateTime(order.shipped_at)}</Field>
          <Field label="Delivered">{fmtDateTime(order.delivered_at)}</Field>
          <Field label="Cancelled">{fmtDateTime(order.cancelled_at)}</Field>
          <Field label="Cancellation reason" testid={`order-cancel-reason-${order.id}`}>{order.cancel_reason}</Field>
          {order.needs_refund && (
            <div className="text-xs font-medium text-red-700">Refund pending</div>
          )}
        </Section>

        <Section title="Payment" testid={`order-payment-${order.id}`}>
          <Field label="Method" testid={`order-payment-method-${order.id}`}>
            {PAYMENT_METHOD_LABEL[order.payment_method] || titleCase(order.payment_method)}
          </Field>
          <Field label="Status">{titleCase(order.payment_status)}</Field>
          <Field label="Paid">{fmtDateTime(order.paid_at)}</Field>
          {isOnline && (
            <>
              <Field label="Razorpay order ID">
                {order.razorpay_order_id && <span className="font-mono text-xs">{order.razorpay_order_id}</span>}
              </Field>
              <Field label="Razorpay payment ID">
                {order.razorpay_payment_id && <span className="font-mono text-xs">{order.razorpay_payment_id}</span>}
              </Field>
            </>
          )}
          <Field label="Payment reference">
            {order.payment_id && <span className="font-mono text-xs">{order.payment_id}</span>}
          </Field>
        </Section>
      </div>

      {history.length > 0 && (
        <Section title="Status history" testid={`order-timeline-${order.id}`}>
          <ol className="space-y-1.5">
            {history.map((h, i) => (
              <li key={`${h.status}-${h.at}-${i}`} className="text-sm flex flex-wrap gap-x-2">
                <span className="font-medium">{titleCase(h.status)}</span>
                <span className="text-xs text-[hsl(var(--kb-muted))]">{fmtDateTime(h.at)}</span>
                {h.note && <span className="text-xs text-[hsl(var(--kb-muted))] w-full">{h.note}</span>}
              </li>
            ))}
          </ol>
        </Section>
      )}

      <div className="-mx-4 -mb-4">
        <OrderItems order={order} />
      </div>
    </div>
  );
}

const STATUSES = ["placed", "confirmed", "processing", "shipped", "delivered", "cancelled", "returned", "refunded"];

export default function AdminOrders() {
  const [filters, setFilters] = useState({ q: "", status: "", payment_status: "", seller_id: "" });
  const [sellers, setSellers] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sellers/admin/all").then(({ data }) => setSellers(data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/orders", { params });
      setData(data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.payment_status, filters.seller_id, page]);

  const updateStatus = async (id, status) => {
    if (!window.confirm(`Change order status to ${status}?`)) return;
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "order_number", label: "Order", render: (r) => <span className="font-mono text-xs">{r.order_number || r.id.slice(0, 8)}</span> },
    {
      // Was an 8-character user_id fragment, which told an admin nothing.
      // Name + phone come from the address embedded in the order itself, so
      // they are visible in the list without expanding the row.
      key: "customer", label: "Customer",
      render: (r) => {
        const a = r.shipping_address || r.address || {};
        return (
          <div className="min-w-[8rem]" data-testid={`order-row-customer-${r.id}`}>
            <div className="text-sm font-medium break-words">{a.name || "—"}</div>
            {a.phone && <div className="text-xs text-[hsl(var(--kb-muted))]">{a.phone}</div>}
            {(a.city || a.pincode) && (
              <div className="text-[11px] text-[hsl(var(--kb-muted))]">
                {[a.city, a.pincode].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        );
      },
    },
    { key: "item_count", label: "Items", render: (r) => r.item_count ?? (r.items?.length || 0) },
    // (products for each order render in the expandable row — see OrderItems)
    { key: "total", label: "Total", render: (r) => money(r.total) },
    { key: "payment_status", label: "Payment", render: (r) => <span className="uppercase text-xs">{r.payment_status}</span> },
    {
      key: "status", label: "Status",
      render: (r) => (
        <select
          className="kb-input !py-1 !px-2 !text-xs"
          value={r.status}
          onChange={(e) => updateStatus(r.id, e.target.value)}
          data-testid={`order-status-${r.id}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
      ),
    },
    { key: "created_at", label: "Placed", render: (r) => <span className="text-xs whitespace-nowrap">{fmtDateTime(r.placed_at || r.created_at)}</span> },
  ];

  return (
    <div data-testid="admin-orders-page">
      <div className="overline mb-2">Fulfilment</div>
      <h1 className="font-display text-4xl mb-2">All Orders ({data.total})</h1>
      <p className="text-sm text-[hsl(var(--kb-muted))] mb-8" data-testid="orders-hint">
        Select <strong>View details</strong> on any order to see the customer, delivery address, payment and products.
      </p>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-4 mb-4 grid md:grid-cols-5 gap-3">
        <input className="kb-input md:col-span-2" placeholder="Search by order number / id…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} data-testid="filter-q" />
        <select className="kb-input" value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }} data-testid="filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="kb-input" value={filters.payment_status} onChange={(e) => { setFilters({ ...filters, payment_status: e.target.value }); setPage(1); }} data-testid="filter-payment">
          <option value="">All payments</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
        </select>
        <select className="kb-input" value={filters.seller_id} onChange={(e) => { setFilters({ ...filters, seller_id: e.target.value }); setPage(1); }} data-testid="filter-seller">
          <option value="">All sellers</option>
          {sellers.map((s) => <option key={s.id} value={s.id}>{s.shop_name}</option>)}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={data.items}
        loading={loading}
        emptyLabel="No orders match"
        testid="orders-table"
        expandHeader="Order details"
        // Spell out what the control reveals, and carry the line count in the
        // label: on a narrow screen the Items column can sit off to the right
        // of the table's horizontal scroll, so the button has to stand alone.
        expandLabel={({ row, open }) =>
          open ? "Hide details" : `View details (${(row.items || []).length})`
        }
        renderExpanded={(row) => <OrderDetails order={row} />}
      />
      <Pagination page={page} limit={20} total={data.total} onPage={setPage} />
    </div>
  );
}
